import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPlans, setSelectedPlan as setSelectedPlanAction, clearError } from '../../features/payment/paymentSlice';

const PLAYER_ROLE = 'player';

// Coach/seat-based packages that must never show up in the player flow
const EXCLUDED_PLAN_NAMES = ['staff package', 'program package'];

// Get a base name by stripping interval-related words
function getBasePlanName(name) {
  return name
    .replace(/\b(monthly|yearly|annual|month|year)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export const PlayerPlanSelectionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Carried over from the player login (team + player select) step
  const { teamId, playerName } = location.state || {};

  const { plans, loading, error } = useSelector((state) => state.payment);
  const [localSelectedPlan, setLocalSelectedPlan] = useState(null);

  // Same plans API as the coach flow, fetched for the player role.
  // Never show coach/seat-based packages here.
  const selectablePlans = plans.filter(
    (plan) => !EXCLUDED_PLAN_NAMES.includes((plan.name || '').toLowerCase().trim())
  );

  const filteredPlans = selectablePlans.filter((plan) => {
    const name = (plan.name || '').toLowerCase().trim();
    const idMatch = (plan.planId || plan._id || '').toLowerCase().includes(PLAYER_ROLE);
    return name.includes(PLAYER_ROLE) || idMatch;
  });

  // Backend already filters by the role header — if the extra client-side
  // filter removes everything, fall back to whatever the API returned
  // (minus the excluded packages).
  const visiblePlans = filteredPlans.length > 0 ? filteredPlans : selectablePlans;

  // Group plans by base name (yearly only is shown)
  const groupedPlans = useMemo(() => {
    const groups = {};
    visiblePlans.forEach((plan) => {
      const baseName = getBasePlanName(plan.name);
      if (!groups[baseName]) {
        groups[baseName] = { baseName, monthly: null, yearly: null };
      }
      const interval = (plan.interval || 'month').toLowerCase();
      if (interval === 'year' || interval === 'yearly' || interval === 'annual') {
        groups[baseName].yearly = plan;
      } else {
        groups[baseName].monthly = plan;
      }
    });
    return Object.values(groups);
  }, [visiblePlans]);

  useEffect(() => {
    // If we got here without the player/team info, send the user back.
    if (!teamId || !playerName) {
      navigate('/auth/login-player');
      return;
    }

    dispatch(fetchPlans(PLAYER_ROLE));

    return () => {
      dispatch(clearError());
    };
  }, [teamId, playerName, navigate, dispatch]);

  const handlePlanSelect = (group) => {
    const plan = group.yearly || group.monthly;
    setLocalSelectedPlan(plan);
    dispatch(setSelectedPlanAction(plan));
  };

  const handleContinue = () => {
    if (!localSelectedPlan) return;

    // Carry the chosen plan into identity verification, which submits the
    // player login + Outseta checkout with this planUid.
    navigate('/auth/identity-verification', {
      state: {
        teamId,
        playerName,
        planUid: localSelectedPlan.planId,
      },
    });
  };

  const isGroupSelected = (group) => {
    if (!localSelectedPlan) return false;
    return group.monthly?.planId === localSelectedPlan.planId || group.yearly?.planId === localSelectedPlan.planId;
  };

  const getActivePlan = (group) => group.yearly || group.monthly;

  const getMergedFeatures = (group) => {
    const features = new Set();
    group.monthly?.features?.forEach((f) => features.add(f));
    group.yearly?.features?.forEach((f) => features.add(f));
    return [...features];
  };

  const getDescription = (group) => {
    const activePlan = getActivePlan(group);
    return activePlan?.description || group.monthly?.description || group.yearly?.description;
  };

  return (
    <main className="min-h-screen bg-jp-page text-jp-ink text-left overflow-x-hidden">
      {/* Navy strip behind the see-through navbar so its white logo and links stay readable */}
      <div className="bg-jp-band h-16 sm:h-20" aria-hidden="true" />

      <section className="px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-16 sm:pb-20">
        <div className="max-w-2xl mx-auto text-center mb-8">
          <p className="text-jp-accent dark:text-jp-gold text-sm font-bold uppercase tracking-widest mb-3">
            Step 2
          </p>
          <h1 className="text-5xl sm:text-6xl font-anton leading-[1.1] tracking-tight text-jp-ink">
            CHOOSE YOUR PLAN
          </h1>
          <p className="mt-4 text-jp-muted text-sm sm:text-base">
            Select the plan that best fits your needs.
          </p>
        </div>

        <div className="max-w-6xl mx-auto bg-jp-card rounded-2xl border border-jp-outline/70 shadow-sm p-5 sm:p-8">
          {/* Error Message */}
          {error && (
            <div className="mb-6 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/40 text-red-700 dark:text-red-300 text-sm text-center">
              {error}
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="py-12 text-center text-jp-muted">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-jp-accent mb-4"></div>
              <p>Loading plans...</p>
            </div>
          ) : (
            <>
              {/* Plans Grid */}
              <div role="radiogroup" aria-label="Plans" className="rounded-xl p-5 sm:p-8 bg-jp-section border border-jp-outline/50 flex flex-col md:flex-row md:flex-wrap justify-center gap-6">
                {groupedPlans.length === 0 ? (
                  <div className="w-full py-6 text-center text-jp-muted">
                    No plans available. Please contact support.
                  </div>
                ) : (
                  groupedPlans.map((group) => {
                    const activePlan = getActivePlan(group);
                    const selected = isGroupSelected(group);
                    const features = getMergedFeatures(group);
                    const description = getDescription(group);

                    return (
                      <div
                        key={group.baseName}
                        role="radio"
                        tabIndex={0}
                        aria-checked={selected}
                        onClick={() => handlePlanSelect(group)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handlePlanSelect(group);
                          }
                        }}
                        className={`relative flex flex-col cursor-pointer w-full md:max-w-md md:flex-1 text-left rounded-2xl p-6 sm:p-8 bg-jp-band text-white border-2 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent focus-visible:ring-offset-2 focus-visible:ring-offset-jp-card ${
                          selected
                            ? 'border-cyan-400 shadow-lg shadow-cyan-500/25'
                            : 'border-transparent hover:border-cyan-400/50'
                        }`}
                      >
                        {/* Selected Badge */}
                        {selected && (
                          <span className="absolute top-5 right-5 inline-flex items-center gap-1.5 rounded-full bg-cyan-400 text-slate-900 text-xs font-bold uppercase tracking-wider px-3 py-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                            Selected
                          </span>
                        )}

                        {/* Plan Name */}
                        <span className="block text-slate-300 text-sm font-bold uppercase tracking-wider mb-2 pr-28">
                          {group.baseName}
                        </span>

                        {/* Active Price */}
                        <span className="block mb-6">
                          <span className="text-5xl font-extrabold text-cyan-400 tabular-nums">
                            ${activePlan?.price}
                          </span>
                          <span className="text-slate-300 text-sm"> / year</span>
                        </span>

                        {/* Plan Description */}
                        {description && (
                          <span
                            className="block mb-6 text-slate-300 text-sm leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: description }}
                          />
                        )}

                        {/* Plan Features */}
                        {features.length > 0 && (
                          <ul className="space-y-2.5">
                            {features.map((feature, index) => (
                              <li key={index} className="flex gap-2 text-slate-200 text-sm">
                                <svg className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {/* Continue Button */}
              <div className="max-w-md mx-auto pt-8">
                <button
                  type="button"
                  onClick={handleContinue}
                  disabled={!localSelectedPlan}
                  className="w-full py-4 text-base font-bold uppercase tracking-wider bg-gradient-to-r from-cyan-400 via-cyan-500 to-blue-600 text-slate-900 rounded-xl hover:brightness-110 transition-all shadow-lg shadow-cyan-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-jp-accent focus-visible:ring-offset-2 focus-visible:ring-offset-jp-card active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Continue
                </button>
              </div>

              {/* Back Link */}
              <div className="pt-5 text-center text-sm sm:text-base">
                <button
                  type="button"
                  onClick={() => navigate('/auth/login-player')}
                  className="font-semibold text-jp-accent hover:underline focus:outline-none focus-visible:underline"
                >
                  Back
                </button>
              </div>
            </>
          )}
        </div>
      </section>
    </main>
  );
};

export default PlayerPlanSelectionPage;
